import React, { useState, useEffect } from 'react';
import { Plus, X, Trash2, AlertCircle, CheckCircle, Upload } from 'lucide-react';
import api from '../utils/api';

type SubjectCategory = 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY';

interface LessonSubject {
  id: string;
  name: string;
  category: SubjectCategory;
}

interface Resource {
  id: string;
  title: string;
  description?: string;
  resourceType: 'HOMEWORK' | 'LESSON_PLAN';
  subjects: {
    id: string;
    name: string;
    category: 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY';
  }[];
}


interface AssignedResource {
  id: string;
  assignmentType: 'LESSON_MATERIAL' | 'HOMEWORK';
  isHomework: boolean;
  assignedAt: string;
  resource: Resource;
}

interface LessonResourceAssignmentProps {
  bookingId: string;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

const LessonResourceAssignment: React.FC<LessonResourceAssignmentProps> = ({
  bookingId,
  isOpen,
  onClose,
  onUpdate
}) => {
  const [availableResources, setAvailableResources] = useState<Resource[]>([]);
  const [assignedResources, setAssignedResources] = useState<AssignedResource[]>([]);
  const [lessonSubject, setLessonSubject] = useState<LessonSubject | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // "Upload new resource" form — the resource auto-inherits this lesson's subject.
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen, bookingId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [availableResponse, assignedResponse] = await Promise.all([
        api.get(`/lesson-resources/available/${bookingId}`),
        api.get(`/lesson-resources/booking/${bookingId}`)
      ]);

      setAvailableResources(availableResponse.data);
      setAssignedResources(assignedResponse.data.resources || []);
      setLessonSubject(assignedResponse.data.booking?.subject || null);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  const uploadAndAssign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadFile || !uploadTitle.trim()) {
      setError('Please choose a file and give it a title.');
      return;
    }

    try {
      setUploading(true);
      setError('');

      const formData = new FormData();
      formData.append('bookingId', bookingId);
      formData.append('title', uploadTitle.trim());
      formData.append('description', uploadDescription.trim());
      formData.append('file', uploadFile);

      await api.post('/lesson-resources/upload-and-assign', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000, // large uploads can exceed the default timeout
      });

      setSuccess('Resource added to the lesson — the parent has been emailed.');
      setUploadFile(null);
      setUploadTitle('');
      setUploadDescription('');
      fetchData();
      onUpdate();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to upload resource');
    } finally {
      setUploading(false);
    }
  };

  const assignResource = async (resourceId: string) => {
    try {
      await api.post('/lesson-resources/assign', {
        bookingId,
        resourceId
      });

      setSuccess('Resource added to the lesson — the parent has been emailed.');
      fetchData(); // Refresh the data
      onUpdate(); // Notify parent component
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to assign resource');
    }
  };


  const removeResource = async (lessonResourceId: string) => {
    try {
      await api.delete(`/lesson-resources/${lessonResourceId}`);
      
      setSuccess('Resource assignment removed!');
      fetchData(); // Refresh the data
      onUpdate(); // Notify parent component
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to remove resource assignment');
    }
  };

  const formatSubjectCategory = (category: 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY') => {
    switch (category) {
      case 'ELEVEN_PLUS':
        return '11+';
      case 'GCSE':
        return 'GCSE';
      case 'A_LEVEL':
        return 'A-Level';
      case 'UNIVERSITY':
        return 'University';
      default:
        return category;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-ink/40 flex items-center justify-center p-4 z-50">
      <div className="bg-paper border border-rule rounded-card max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-rule">
          <h2 className="text-xl font-semibold text-ink">Assign Resources</h2>
          <button
            onClick={onClose}
            className="text-muted hover:text-ink"
          >
            <X className="w-6 h-6" />
          </button>
        </div>


        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Success/Error Messages */}
          {success && (
            <div className="m-6 mb-4 bg-emerald-50 border border-emerald-200 rounded-card p-4 flex items-center">
              <CheckCircle className="w-5 h-5 text-emerald-600 mr-3 flex-shrink-0" />
              <p className="text-emerald-800">{success}</p>
            </div>
          )}

          {error && (
            <div className="m-6 mb-4 bg-red-50 border border-red-200 rounded-card p-4 flex items-center">
              <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0" />
              <p className="text-red-800">{error}</p>
              <button
                onClick={() => setError('')}
                className="ml-auto text-red-600 hover:text-red-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ochre mx-auto"></div>
              <p className="text-muted mt-2">Loading resources...</p>
            </div>
          ) : (
            <div className="p-6 space-y-6">
              {/* Upload a brand-new resource straight onto this lesson */}
              <div className="border border-ochre/40 bg-ochre/5 rounded-card p-4">
                <div className="flex items-center mb-1">
                  <Upload className="w-5 h-5 text-ochre mr-2" />
                  <h3 className="text-lg font-medium text-ink">Upload new resource</h3>
                </div>
                <p className="text-sm text-muted mb-4">
                  Uploads straight onto this lesson.
                  {lessonSubject && (
                    <>
                      {' '}It'll be tagged{' '}
                      <span className="font-medium text-ink">
                        {lessonSubject.name} · {formatSubjectCategory(lessonSubject.category)}
                      </span>{' '}
                      automatically.
                    </>
                  )}
                </p>

                <form onSubmit={uploadAndAssign} className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-ink mb-1">Title</label>
                    <input
                      type="text"
                      value={uploadTitle}
                      onChange={(e) => setUploadTitle(e.target.value)}
                      placeholder="e.g. Week 3 fractions worksheet"
                      className="w-full border border-rule rounded-card px-3 py-2 text-sm bg-paper"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-ink mb-1">Description <span className="text-muted font-normal">(optional)</span></label>
                    <input
                      type="text"
                      value={uploadDescription}
                      onChange={(e) => setUploadDescription(e.target.value)}
                      placeholder="A short note for the student/parent"
                      className="w-full border border-rule rounded-card px-3 py-2 text-sm bg-paper"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-ink mb-1">File</label>
                    <input
                      type="file"
                      onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                      className="w-full text-sm text-muted file:mr-3 file:py-2 file:px-3 file:rounded-card file:border-0 file:text-sm file:bg-ochre/15 file:text-ochre hover:file:bg-ochre/25"
                      required
                    />
                  </div>

                  <p className="text-xs text-muted">
                    The parent is emailed automatically with a one-click download link when you add it.
                  </p>

                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={uploading || !uploadFile || !uploadTitle.trim()}
                      className="btn btn-primary flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploading ? 'Uploading…' : 'Upload & add to lesson'}
                    </button>
                  </div>
                </form>
              </div>

              {/* Currently Assigned Resources */}
              <div>
                <h3 className="text-lg font-medium text-ink mb-4">Assigned Resources</h3>
                {assignedResources.length === 0 ? (
                  <p className="text-muted text-center py-4 bg-cream border border-rule rounded-card">
                    No resources assigned to this lesson yet.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {assignedResources.map((assignment) => (
                      <div key={assignment.id} className="border border-rule rounded-card p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h4 className="font-medium text-ink">{assignment.resource.title}</h4>
                            </div>
                            {assignment.resource.description && (
                              <p className="text-sm text-muted mb-2">{assignment.resource.description}</p>
                            )}
                            <div className="flex items-center space-x-4 text-xs text-muted">
                              {assignment.resource.subjects && assignment.resource.subjects.length > 0 && (
                                <span>{assignment.resource.subjects[0].name} - {formatSubjectCategory(assignment.resource.subjects[0].category)}</span>
                              )}
                              <span>Assigned: {new Date(assignment.assignedAt).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <button
                            onClick={() => removeResource(assignment.id)}
                            className="ml-4 text-red-600 hover:text-red-800"
                            title="Remove assignment"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Available Resources to Assign */}
              <div>
                <h3 className="text-lg font-medium text-ink mb-4">Available Resources</h3>
                {availableResources.length === 0 ? (
                  <p className="text-muted text-center py-4 bg-cream border border-rule rounded-card">
                    No additional resources available for this subject.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {availableResources.map((resource) => (
                      <div key={resource.id} className="border border-rule rounded-card p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-ink mb-2">{resource.title}</h4>
                            {resource.description && (
                              <p className="text-sm text-muted mb-2">{resource.description}</p>
                            )}
                            <div className="flex items-center space-x-4 text-xs text-muted">
                              {resource.subjects && resource.subjects.length > 0 && (
                                <span>{resource.subjects[0].name} - {formatSubjectCategory(resource.subjects[0].category)}</span>
                              )}
                            </div>
                          </div>
                          <div className="ml-4 flex flex-col space-y-2">
                            <button
                              onClick={() => assignResource(resource.id)}
                              className="btn btn-sm btn-primary flex items-center"
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Add to lesson
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-rule p-6">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="btn btn-secondary"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LessonResourceAssignment;